import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import cn from 'classnames';
import { MdClear } from '@react-icons/all-files/md/MdClear';
import { MdSearch } from '@react-icons/all-files/md/MdSearch';
import ReactTooltip from 'react-tooltip';

const SelectField = ({
                       placeholder, options, className, value, onChange, notMulti = false, renderValue = false
                     }) => {
  return <div className='md:m-2 lg:m-0'>
    <Select options={options} isMulti={!notMulti} placeholder={placeholder} isClearable={false}
            controlShouldRenderValue={renderValue} isSearchable={false}
            className={cn('input-border focus-within:border-primary', className)}
            noOptionsMessage={() => 'Tomt.'}
            value={value}
            onChange={onChange}
            theme={theme => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary75: '#FCFCFD',
                primary50: '#FCFCFD',
                primary25: '#FCFCFD',
                primary: '#FFC600'
              }
            })}
            styles={{
              input: styles => ({ ...styles, paddingTop: 4, paddingBottom: 4, borderWidth: 0 }),
              control: styles => ({
                ...styles,
                borderWidth: 0,
                borderRadius: 8,
                '&:hover': {
                  border: 'none'
                }
              })
            }} />
  </div>;
};

const FilterBox = ({ label, remove }) => {
  return <div className='border-2 border-black rounded-lg px-2 py-1 flex items-center space-x-2 m-2'>
    <p>{label}</p>
    <button
      className='outline-none focus:outline-none text-black hover:text-primary transition-colors duration-150'
      onClick={remove}>
      <MdClear className='w-4 h-4' />
    </button>
  </div>;
};

const Search = ({
                  posts,
                  setPosts,
                  selectedIndustries,
                  selectedTypes,
                  selectedAreas,
                  selectedEmployerTypes,
                  selectedPeriod,
                  setSelectedIndustries,
                  setSelectedTypes,
                  setSelectedAreas,
                  setSelectedPeriod,
                  setSelectedEmployerTypes
                }) => {
  const industryOptions = Array.from(new Set(posts.map(post => post.industry))).map(industry => ({
    value: industry,
    label: industry
  }));

  const workTypeOptions = Array.from(new Set(posts.map(post => post.type))).map(type => ({
    value: type,
    label: type
  }));

  const periodOptions = Array.from(new Set(posts.map(post => post.minPeriod))).sort().map(minPeriod => ({
    value: minPeriod,
    label: `T-${minPeriod}`
  }));

  const areaOptions = Array.from(new Set(posts.map(post => post.area))).map(area => ({
    value: area,
    label: area
  }));

  const employerTypeOptions = Array.from(new Set(posts.map(post => post.employerType))).map(employerType => ({
    value: employerType,
    label: employerType
  }));

  const sortByOptions = useMemo(() => ([
    { value: 'latest', label: 'Senaste' },
    { value: 'employer', label: 'Arbetsgivare' },
    { value: 'title', label: 'Rubrik' },
    { value: 'oldest', label: 'Äldsta' }
  ]), []);

  const [sortBy, setSortBy] = useState(sortByOptions[0]);
  const [search, setSearch] = useState('');
  const [lastApplicationDate, setLastApplicationDate] = useState('');

  const removeFilter = (setter, filter) => {
    return () => setter(prev => prev.filter(ele => ele.value !== filter));
  };

  const includesOrEmpty = (selected, value) => {
    return selected.length === 0 || selected.some(ele => ele.value === value);
  };

  useEffect(() => {
    const filteredPosts = posts.filter(post => post.employer.toLowerCase().includes(search.toLowerCase())
      && (!lastApplicationDate || new Date(post.lastApplicationDate).getTime() < new Date(lastApplicationDate).getTime())
      && includesOrEmpty(selectedIndustries, post.industry)
      && includesOrEmpty(selectedTypes, post.type)
      && includesOrEmpty(selectedAreas, post.area)
      && includesOrEmpty(selectedEmployerTypes, post.employerType)
      && (!selectedPeriod || post.minPeriod <= selectedPeriod.value));
    const sorted = filteredPosts.sort((a, b) => {
      if (a.prioritized && b.prioritized && typeof a.priority === 'number' && typeof b.priority === 'number') {
        return a.priority - b.priority;
      } else if (a.prioritized && !b.prioritized) {
        return -1;
      } else if (!a.prioritized && b.prioritized) {
        return 1;
      }

      if (sortBy.value === 'latest') {
        return new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime() ? -1 : 1;
      } else if (sortBy.value === 'oldest') {
        return new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime() ? -1 : 1;
      } else if (sortBy.value === 'employer') {
        return a.employer.localeCompare(b.employer);
      } else if (sortBy.value === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
    setPosts(sorted);
  }, [search, lastApplicationDate, selectedIndustries, selectedTypes, selectedAreas, selectedEmployerTypes, selectedPeriod, posts, setPosts, sortBy]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [selectedIndustries, selectedTypes, selectedAreas, selectedEmployerTypes, selectedPeriod]);

  const numFilters = selectedIndustries.length + selectedAreas.length + selectedTypes.length + selectedEmployerTypes.length + !!selectedPeriod;
  return <div className='flex flex-col w-full pt-12 space-y-2 max-w-screen-xl mx-auto'>
    <div
      className='flex flex-col xl:flex-row justify-between space-y-2 xl:space-y-0 lg:space-x-18 px-12 md:px-0 items-stretch'>
      <div
        className='flex flex-col md:flex-row md:flex-wrap lg:flex-no-wrap md:justify-center md:items-center space-y-2 md:space-y-0 lg:space-x-2 flex-grow w-full md:px-0 md:w-auto md:mx-auto lg:items-start lg:justify-start xl:mx-0'>
        <SelectField options={industryOptions} placeholder='Branch' className='md:w-36'
                     value={selectedIndustries}
                     onChange={setSelectedIndustries} />
        <SelectField options={workTypeOptions} placeholder='Anställningsform' className='md:w-52'
                     value={selectedTypes}
                     onChange={setSelectedTypes} />
        <SelectField options={periodOptions} placeholder='Termin' className='md:w-44' value={selectedPeriod}
                     onChange={setSelectedPeriod} notMulti />
        <SelectField options={areaOptions} placeholder='Ort' className='md:w-44' value={selectedAreas}
                     onChange={setSelectedAreas} />
        <SelectField options={employerTypeOptions} placeholder='Arbetsgivare' className='md:w-44'
                     value={selectedEmployerTypes}
                     onChange={setSelectedEmployerTypes} />
      </div>
      <label
        className='input-border flex justify-between items-center group md:mx-auto lg:ml-auto lg:mr-14 xl:mx-0'>
        <input className='px-2 py-1 rounded-lg outline-none focus:outline-none flex-grow'
               placeholder='Sök företagsnamn'
               value={search}
               onChange={e => setSearch(e.target.value)} />
        <MdSearch
          className='w-6 h-6 group-hover:text-gray-300 group-hover:group-focus-within:text-primary group-focus-within:text-primary transition-colors duration-150 mx-1' />
      </label>
    </div>
    <div
      className={cn('flex flex-wrap lg:-ml-2 p-2 xl:p-0 justify-center xl:justify-start bg-gray-50 xl:bg-white bg-opacity-50', { 'hidden': numFilters === 0 })}>
      {selectedIndustries.map(industry => (
        <FilterBox label={industry.label} remove={removeFilter(setSelectedIndustries, industry.value)}
                   key={industry.value} />
      ))}
      {selectedTypes.map(type => (
        <FilterBox label={type.label} remove={removeFilter(setSelectedTypes, type.value)}
                   key={type.value} />
      ))}
      {selectedPeriod && (
        <FilterBox label={selectedPeriod.label} remove={() => setSelectedPeriod(null)}
                   key={selectedPeriod.value} />
      )}
      {selectedAreas.map(area => (
        <FilterBox label={area.label} remove={removeFilter(setSelectedAreas, area.value)}
                   key={area.value} />
      ))}
      {selectedEmployerTypes.map(employerType => (
        <FilterBox label={employerType.label} remove={removeFilter(setSelectedEmployerTypes, employerType.value)}
                   key={employerType.value} />
      ))}
      <button className='my-auto outline-none focus:outline-none hover:text-primary transition-colors duration-150'
              onClick={() => {
                setSelectedPeriod(null);
                setSelectedEmployerTypes([]);
                setSelectedAreas([]);
                setSelectedIndustries([]);
                setSelectedTypes([]);
              }} data-tip='Rensa sökparametrar'>
        <MdClear className='w-6 h-6' />
        <p className='sr-only'>Rensa sökparametrar</p>
      </button>
    </div>
    <div
      className='flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 md:space-x-2 items-center md:items-stretch mx-auto lg:mr-14 xl:mx-0'>
      <div className='flex items-center'>
        <p>Sista ansökningsdatum: </p>
      </div>
      <input type='date' className='input-border px-2 bg-white' value={lastApplicationDate}
             onChange={e => setLastApplicationDate(e.target.value)} />
      <SelectField notMulti onChange={setSortBy} value={sortBy} className='w-44' options={sortByOptions}
                   placeholder='Sortering' renderValue />
    </div>
    <ReactTooltip className='!rounded-lg max-w-sm text-center bg-black text-white shadow' />
  </div>;
};

export default Search;